import { useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../api/client'
import type { CIBusinessServices } from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { useAsync } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'
import { CRITICALITY_LABEL } from '../pages/Services'
import { Card, EmptyState, ErrorBanner, Spinner, StatusBadge, timeAgo } from './common'
import { DataTable } from './DataTable'

/**
 * Business services from both sides: the CI page (which services this CI is
 * part of, as what, on which port and process, and the criticality it
 * inherits) and the service page (its dependencies, with tags to put on
 * them). Both read the members the orchestrator saves from the composition
 * every hour (service_membership_service).
 */

export function roleLabel(role: string): string {
  if (role === 'entry') return t('ingresso')
  if (role === 'component') return t('componente')
  if (role === 'app') return t('Business App')
  return t('infrastruttura')
}

/** The CI page section. */
export function SezioneServiziCI({ dati }: { dati: CIBusinessServices }) {
  return (
    <Card title={t('Business service ({n})', { n: dati.services.length })}>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('I servizi di business di cui questo CI fa parte, ricavati dalla composizione di ciascun servizio (ingressi, catena delle chiamate, infrastruttura sotto) e aggiornati ogni ora.')}{' '}
        {dati.infrastructure_inherits
          ? t('Il CI eredita la criticità più alta fra i suoi servizi, anche come infrastruttura.')
          : t('Il CI eredita la criticità più alta fra i servizi di cui è ingresso o componente; come infrastruttura li sostiene senza ereditarla, perché è condivisa da molti servizi.')}
      </p>
      {dati.inherited_criticality && (
        <p>
          {t('Criticità ereditata')}: <StatusBadge status={CRITICALITY_LABEL[dati.inherited_criticality] ?? dati.inherited_criticality} />
        </p>
      )}
      {dati.services.length > 0 && (
        <DataTable
          rows={dati.services}
          rowKey={(s) => s.service_id}
          conteggio={{ nome: t('servizi') }}
          columns={[
            {
              key: 'name', header: t('Servizio'), value: (s) => [s.name, s.owner].filter(Boolean).join(' '),
              render: (s) => <><Link to={`/services/${s.service_id}`}>{s.name}</Link>{s.owner && <><br /><small className="text-muted">{s.owner}</small></>}</>,
            },
            {
              key: 'criticality', header: t('Criticità'), value: (s) => CRITICALITY_LABEL[s.criticality] ?? s.criticality,
              render: (s) => (
                <>
                  {CRITICALITY_LABEL[s.criticality] ?? s.criticality}
                  {!s.inherits && <><br /><small className="text-muted">{t('non ereditata')}</small></>}
                </>
              ),
            },
            {
              key: 'role', header: t('Ruolo'), value: (s) => roleLabel(s.role),
              render: (s) => (
                <>
                  {roleLabel(s.role)}
                  {s.role === 'component' && s.depth != null && <small className="text-muted"> · {t('livello {n}', { n: s.depth })}</small>}
                  {s.band && <small className="text-muted"> · {s.band}</small>}
                </>
              ),
            },
            {
              key: 'ports', header: t('Porte, processo'),
              value: (s) => s.ports.map((p) => [p.port, p.name, p.process, p.software?.name, p.software?.version].filter(Boolean).join(' ')).join(' '),
              render: (s) => (
                s.ports.length === 0 ? '—' : s.ports.map((p) => (
                  <div key={p.port}>
                    <span className="mono">{p.port}</span>{p.name && <small className="text-muted"> {p.name}</small>}
                    {p.process ? <> · <span className="mono" title={p.path ?? undefined}>{p.process}</span></> : <small className="text-muted"> · {t('processo non noto')}</small>}
                    {p.software && (
                      <> → {p.software.name}{p.software.version && ` ${p.software.version}`}
                        {!p.software.matched && <small className="text-muted" title={t('Dichiarato dall’eseguibile, non trovato nell’inventario software del CI')}> ({t('dall’eseguibile')})</small>}
                      </>
                    )}
                  </div>
                ))
              ),
            },
            {
              key: 'first_seen', header: t('Da quando'), type: 'date',
              render: (s) => <small>{s.first_seen ? new Date(s.first_seen).toLocaleDateString(localeCorrente()) : '—'}</small>,
            },
          ]}
        />
      )}
      {(dati.apps ?? []).length > 0 && (
        <>
          <h4>{t('Business App')}</h4>
          <ul>
            {(dati.apps ?? []).map((a) => (
              <li key={a.id}>
                <Link to={`/business-apps/${a.id}`}>{a.name}</Link>
                {a.criticality && <> · {CRITICALITY_LABEL[a.criticality] ?? a.criticality}</>}
                <small className="text-muted"> · {a.elements.map((e) => e.name || e.ports.join(', ')).join(', ')}</small>
              </li>
            ))}
          </ul>
        </>
      )}
      <p className="field__hint">
        {t('Il processo in ascolto su una porta e il software a cui appartiene arrivano dall’inventario avanzato (SSH o WinRM): su Linux il pacchetto che contiene l’eseguibile, su Windows il prodotto dichiarato dall’eseguibile, abbinati all’inventario software del CI. Sugli apparati senza inventario avanzato non sono noti.')}
      </p>
    </Card>
  )
}

/** The service page card: dependencies, tags on them, recompute now. */
export function DipendenzeServizio({ serviceId }: { serviceId: string }) {
  const { principal } = useAuth()
  const ruoli = principal?.roles ?? []
  const scrive = ruoli.some((r) => ['tenant_admin', 'tenant_operator', 'provider_admin'].includes(r))
  const admin = ruoli.includes('tenant_admin') || ruoli.includes('provider_admin')
  const dati = useAsync(() => api.serviceMembers(serviceId), [serviceId])
  const impostazioni = useAsync(() => api.criticalitySettings(), [])
  const [scelti, setScelti] = useState<Set<string>>(new Set())
  const [ruolo, setRuolo] = useState('')
  const [tag, setTag] = useState('')
  const [lavoro, setLavoro] = useState(false)
  const [esito, setEsito] = useState<string | null>(null)
  const [errore, setErrore] = useState<unknown>(null)

  const membri = (dati.data?.members ?? []).filter((m) => !ruolo || m.role === ruolo)

  async function esegui(fn: () => Promise<unknown>) {
    setErrore(null)
    setEsito(null)
    setLavoro(true)
    try {
      await fn()
    } catch (e) {
      setErrore(e)
    } finally {
      setLavoro(false)
    }
  }

  const ricalcola = () => esegui(async () => {
    const r = await api.refreshServiceMembers(serviceId)
    setEsito(t('Dipendenze ricalcolate: {n} ({a} nuove, {r} uscite).', { n: r.members, a: r.added, r: r.removed }))
    dati.reload()
  })

  const metti = () => esegui(async () => {
    const etichette = tag.split(',').map((x) => x.trim()).filter(Boolean)
    if (etichette.length === 0) return
    const r = await api.tagServiceMembers(serviceId, {
      tags: etichette,
      ci_ids: scelti.size > 0 ? [...scelti] : membri.map((m) => m.ci_id),
    })
    setEsito(t('Tag messi su {n} dipendenze ({a} nuove assegnazioni).', { n: r.cis, a: r.assigned }))
    setTag('')
  })

  return (
    <Card
      title={t('Dipendenze ({n})', { n: dati.data?.members.length ?? 0 })}
      actions={scrive && <button type="button" className="secondary" onClick={() => void ricalcola()} disabled={lavoro}>{t('Ricalcola ora')}</button>}
    >
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('I CI che compongono il servizio, salvati dalla composizione ogni ora: ingressi, componenti della catena delle chiamate e l’infrastruttura sotto. Ogni CI li vede nella sua sezione «Business service» e ne eredita la criticità.')}
        {dati.data?.refreshed_at && ` ${t('Ultimo calcolo: {q}.', { q: timeAgo(dati.data.refreshed_at) })}`}
      </p>
      <ErrorBanner error={dati.error} />
      <ErrorBanner error={errore} />
      {esito && <p className="field__hint">{esito}</p>}
      {dati.loading && !dati.data && <Spinner />}
      {dati.data && dati.data.members.length === 0 && (
        <EmptyState title={t('Nessuna dipendenza salvata')} hint={t('Si calcolano entro un’ora dalla creazione del servizio, oppure subito con «Ricalcola ora».')} />
      )}
      {dati.data && dati.data.members.length > 0 && (
        <>
          <div className="toolbar" style={{ marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
            <label className="field__inline">
              {t('Ruolo')}
              <select value={ruolo} onChange={(e) => { setRuolo(e.target.value); setScelti(new Set()) }}>
                <option value="">{t('Tutti')}</option>
                <option value="entry">{t('ingresso')}</option>
                <option value="component">{t('componente')}</option>
                <option value="infrastructure">{t('infrastruttura')}</option>
              </select>
            </label>
            {scrive && (
              <>
                <input value={tag} onChange={(e) => setTag(e.target.value)} placeholder={t('chiave:valore, separati da virgola')}
                       aria-label={t('Tag da mettere')} style={{ minWidth: 240 }} />
                <button type="button" onClick={() => void metti()} disabled={lavoro || !tag.trim()}>
                  {scelti.size > 0 ? t('Metti il tag alle {n} scelte', { n: scelti.size }) : t('Metti il tag a tutte quelle elencate ({n})', { n: membri.length })}
                </button>
              </>
            )}
          </div>
          <DataTable
            rows={membri}
            rowKey={(m) => m.ci_id}
            conteggio={{ nome: t('dipendenze') }}
            selezione={scrive ? { chiavi: scelti, onCambia: setScelti, id: (m) => m.ci_id } : undefined}
            columns={[
              {
                key: 'name', header: t('CI'), value: (m) => [m.name ?? m.ci_id, m.ci_class].filter(Boolean).join(' '),
                render: (m) => <><Link to={`/cis/${m.ci_id}`}>{m.name ?? m.ci_id}</Link>{m.ci_class && <><br /><small className="text-muted">{m.ci_class}</small></>}</>,
              },
              {
                key: 'role', header: t('Ruolo'), value: (m) => roleLabel(m.role),
                render: (m) => (
                  <>
                    {roleLabel(m.role)}
                    {m.role === 'component' && m.depth != null && <small className="text-muted"> · {t('livello {n}', { n: m.depth })}</small>}
                    {m.band && <small className="text-muted"> · {m.band}</small>}
                  </>
                ),
              },
              { key: 'ports', header: t('Porte'), mono: true, value: (m) => m.ports.join(', ') },
              {
                key: 'software', header: t('Software'), value: (m) => (m.software ?? []).join(', '),
                render: (m) => <small>{m.software?.length ? m.software.join(', ') : '—'}</small>,
              },
              {
                key: 'first_seen', header: t('Da quando'), type: 'date',
                render: (m) => <small>{m.first_seen ? new Date(m.first_seen).toLocaleDateString(localeCorrente()) : '—'}</small>,
              },
            ]}
          />
        </>
      )}
      {impostazioni.data && (
        <label className="field__inline" style={{ marginTop: 12 }}>
          <input type="checkbox" checked={impostazioni.data.infrastructure_inherits} disabled={!admin || lavoro}
                 onChange={(e) => void esegui(async () => {
                   await api.saveCriticalitySettings(e.target.checked)
                   impostazioni.reload()
                 })} />
          {' '}{t('Anche l’infrastruttura (host, cluster, datastore, rete, storage) eredita la criticità dei servizi che sostiene. Vale per tutti i servizi del tenant.')}
        </label>
      )}
    </Card>
  )
}
